import { ComponentType, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CloudOffRounded from '@mui/icons-material/CloudOffRounded';
import LockClockRounded from '@mui/icons-material/LockClockRounded';
import SentimentDissatisfiedRounded from '@mui/icons-material/SentimentDissatisfiedRounded';
import SystemUpdateAltRounded from '@mui/icons-material/SystemUpdateAltRounded';
import WifiOffRounded from '@mui/icons-material/WifiOffRounded';
import { Button, Collapse, SvgIconProps } from '@mui/material';

import { ReactComponent as ForbiddenImage } from '@/assets/Fingerprintsecurity.svg';
import { ReactComponent as NotFoundImage } from '@/assets/Rocketscienceerror404.svg';
import { ErrorKind } from '@/shared/lib/errors';

import {
  Actions,
  Description,
  DetailsContainer,
  DetailsPre,
  IconCircle,
  Root,
  Title,
} from './ErrorPage.styles';

export type ErrorPageVariant =
  | 'notFound'
  | 'forbidden'
  | 'crash'
  | 'server'
  | 'network'
  | 'authUnavailable'
  | 'updated';

export interface ErrorPageProps {
  variant: ErrorPageVariant;
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  error?: unknown;
  fullScreen?: boolean;
}

const VARIANT_ICONS: Partial<Record<ErrorPageVariant, ComponentType<SvgIconProps>>> = {
  crash: SentimentDissatisfiedRounded,
  server: CloudOffRounded,
  network: WifiOffRounded,
  authUnavailable: LockClockRounded,
  updated: SystemUpdateAltRounded,
};

const VARIANT_COPY_KEYS: Record<ErrorPageVariant, { title: string; description: string }> = {
  notFound: { title: 'Oops! Lost in Ad Space?', description: 'not_found_description' },
  forbidden: { title: 'access_denied', description: 'error_page.forbidden.description' },
  crash: { title: 'error_page.crash.title', description: 'error_page.crash.description' },
  server: { title: 'error_page.server.title', description: 'error_page.server.description' },
  network: { title: 'error_page.network.title', description: 'error_page.network.description' },
  authUnavailable: {
    title: 'error_page.authUnavailable.title',
    description: 'error_page.authUnavailable.description',
  },
  updated: { title: 'error_page.updated.title', description: 'error_page.updated.description' },
};

function ErrorIllustration({ variant }: { variant: ErrorPageVariant }) {
  if (variant === 'notFound') return <NotFoundImage />;
  if (variant === 'forbidden') return <ForbiddenImage />;

  const Icon = VARIANT_ICONS[variant] ?? SentimentDissatisfiedRounded;

  return (
    <IconCircle>
      <Icon color="primary" sx={{ fontSize: '3.5rem' }} />
    </IconCircle>
  );
}

function ErrorDetails({ error }: { error: unknown }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!import.meta.env.DEV || error === undefined) return null;

  const name = error instanceof Error ? error.name : typeof error;
  const message = error instanceof Error ? error.message : String(error);
  const stackLines =
    error instanceof Error && error.stack ? error.stack.split('\n').slice(0, 10) : [];

  return (
    <DetailsContainer>
      <Button size="small" onClick={() => setOpen((prev) => !prev)}>
        {t('error_page.details')}
      </Button>
      <Collapse in={open}>
        <DetailsPre>
          {name}: {message}
          {stackLines.length > 0 ? `\n${stackLines.join('\n')}` : ''}
        </DetailsPre>
      </Collapse>
    </DetailsContainer>
  );
}

export function ErrorPage({
  variant,
  title,
  description,
  actions,
  error,
  fullScreen = false,
}: ErrorPageProps) {
  const { t } = useTranslation();
  const copy = VARIANT_COPY_KEYS[variant];

  return (
    <Root data-testid={`error-page-${variant}`} fullScreen={fullScreen}>
      <ErrorIllustration variant={variant} />
      <Title>{title ?? t(copy.title)}</Title>
      <Description>{description ?? t(copy.description)}</Description>
      {actions ? <Actions>{actions}</Actions> : null}
      <ErrorDetails error={error} />
    </Root>
  );
}

export function errorKindToVariant(kind: ErrorKind): ErrorPageVariant {
  switch (kind) {
    case 'notFound':
      return 'notFound';
    case 'forbidden':
      return 'forbidden';
    case 'network':
      return 'network';
    case 'server':
      return 'server';
    case 'chunk':
      return 'updated';
    case 'unauthorized':
    case 'unknown':
    default:
      return 'crash';
  }
}
